import { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { Exercise } from '../../types';
import { ExerciseAutocomplete } from './ExerciseAutocomplete';
import { TemplateItemDrawer } from './TemplateItemDrawer';
import { Button } from '../ui/Button';
import { CARDS, INTERACTIVE } from '../../styles/classNames';

export interface TemplateItemDraft {
  exerciseId: string;
  sets?: number;
  reps?: number;
  restSec?: number;
  weightSuggestion?: string | null;
  customCues?: string;
}

interface TemplateBuilderProps {
  exercises: Exercise[];
  exerciseLookup: Map<string, Exercise>;
  templateName: string;
  templateNotes: string;
  setTemplateName: (value: string) => void;
  setTemplateNotes: (value: string) => void;
  templateItems: TemplateItemDraft[];
  onAddItem: (item: TemplateItemDraft) => void;
  onUpdateItem: (index: number, item: TemplateItemDraft) => void;
  onReorderItems: (oldIndex: number, newIndex: number) => void;
  onRemoveItem: (index: number) => void;
  onSave: () => void;
  mode?: 'create' | 'edit';
}

interface SortableItemProps {
  item: TemplateItemDraft;
  idx: number;
  exerciseName: string;
  onEdit: () => void;
  onRemove: () => void;
}

function SortableItem({ item, idx, exerciseName, onEdit, onRemove }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `${item.exerciseId}_${idx}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Format compact summary: "3x8 @ 90s"
  const summary = `${item.sets || 3}x${item.reps || 8} @ ${item.restSec || 90}s`;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${CARDS.nested} flex items-center gap-2 md:gap-3 p-2 md:p-3`}
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className={`cursor-grab active:cursor-grabbing shrink-0 w-8 h-8 md:w-6 md:h-6 flex items-center justify-center rounded-md border border-gray-200 dark:border-white/10 ${INTERACTIVE.glassHover} text-base md:text-xs touch-none`}
        aria-label="Drag to reorder"
      >
        ⠿
      </button>

      {/* Clickable content area */}
      <button
        onClick={onEdit}
        className="flex-1 min-w-0 text-left py-1 hover:bg-white/5 rounded-md transition -my-1 px-1"
      >
        <p className="font-medium truncate">
          {idx + 1}. {exerciseName}
        </p>
        <p className="text-xs text-slate-400">
          {summary}
          {item.weightSuggestion ? ` · ${item.weightSuggestion}` : ''}
        </p>
      </button>

      {/* Remove button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="shrink-0 text-xs text-red-300 hover:text-red-200 px-2 py-1"
        aria-label="Remove exercise"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

export function TemplateBuilder({
  exercises,
  exerciseLookup,
  templateName,
  templateNotes,
  setTemplateName,
  setTemplateNotes,
  templateItems,
  onAddItem,
  onUpdateItem,
  onReorderItems,
  onRemoveItem,
  onSave,
  mode = 'create',
}: TemplateBuilderProps) {
  // Drawer state
  const [drawerItemIndex, setDrawerItemIndex] = useState<number | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px of movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = templateItems.findIndex(
        (_, idx) => `${templateItems[idx].exerciseId}_${idx}` === active.id
      );
      const newIndex = templateItems.findIndex(
        (_, idx) => `${templateItems[idx].exerciseId}_${idx}` === over.id
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        onReorderItems(oldIndex, newIndex);
      }
    }
  };

  // Quick add: select exercise → add to template with defaults
  const handleQuickAdd = (exercise: Exercise) => {
    const newItem: TemplateItemDraft = {
      exerciseId: exercise.id,
      sets: exercise.defaultSets || 3,
      reps: exercise.defaultReps || 8,
      restSec: exercise.defaultRestSec || 90,
      weightSuggestion: '',
      customCues: exercise.defaultCues || '',
    };
    onAddItem(newItem);
    // Clear validation error when exercises are added
    if (validationError?.includes('exercise')) {
      setValidationError(null);
    }
  };

  // Drawer handlers
  const drawerItem = drawerItemIndex !== null ? templateItems[drawerItemIndex] : null;
  const drawerExercise = drawerItem ? exerciseLookup.get(drawerItem.exerciseId) || null : null;

  const handleDrawerClose = (updatedItem: TemplateItemDraft) => {
    if (drawerItemIndex !== null) {
      onUpdateItem(drawerItemIndex, updatedItem);
    }
    setDrawerItemIndex(null);
  };

  const handleDrawerDelete = () => {
    if (drawerItemIndex !== null) {
      onRemoveItem(drawerItemIndex);
    }
    setDrawerItemIndex(null);
  };

  // Validation on save
  const handleSave = () => {
    setTouched(true);
    
    if (!templateName.trim()) {
      setValidationError('Template name is required');
      return;
    }
    
    if (templateItems.length === 0) {
      setValidationError('Add at least one exercise to the template');
      return;
    }
    
    setValidationError(null);
    onSave();
  };

  const nameIsInvalid = touched && !templateName.trim();
  const noExercises = touched && templateItems.length === 0;

  return (
    <div className="space-y-4">
      {/* Validation Error Banner */}
      {validationError && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-700 dark:text-red-300 text-sm">
          {validationError}
        </div>
      )}

      {/* Save button - always visible but validates on click */}
      <Button 
        onClick={handleSave} 
        className="w-full" 
        size="lg"
        data-testid="template-save-button"
      >
        {mode === 'edit' ? 'Save changes' : 'Save template'}
      </Button>

      {/* Template name & notes */}
      <div className="space-y-3">
        <label className="flex flex-col gap-2 text-sm text-slate-300">
          <span className="text-xs uppercase tracking-wide text-slate-400">
            Template name <span className="text-red-400">*</span>
          </span>
          <input
            className={`form-field ${nameIsInvalid ? 'border-red-500 dark:border-red-500 focus:ring-red-500' : ''}`}
            placeholder="e.g. Lower Body Strength A"
            data-testid="template-name-input"
            value={templateName}
            onChange={(e) => {
              setTemplateName(e.target.value);
              if (validationError?.includes('name') && e.target.value.trim()) {
                setValidationError(null);
              }
            }}
            onBlur={() => setTouched(true)}
          />
          {nameIsInvalid && (
            <p className="text-xs text-red-400">Template name is required</p>
          )}
        </label>
        <label className="flex flex-col gap-2 text-sm text-slate-300">
          <span className="text-xs uppercase tracking-wide text-slate-400">Template notes (private)</span>
          <input
            className="form-field"
            placeholder="Notes for you (not shown to clients)"
            value={templateNotes}
            onChange={(e) => setTemplateNotes(e.target.value)}
          />
        </label>
      </div>

      {/* Quick add exercise */}
      <div className={`${CARDS.nested} space-y-3 ${noExercises ? 'border-red-500 dark:border-red-500' : ''}`}>
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-400">
            Add exercises <span className="text-red-400">*</span>
          </p>
          <p className="text-sm text-slate-300">Search and tap to add</p>
        </div>

        <ExerciseAutocomplete
          exercises={exercises}
          onSelect={handleQuickAdd}
          placeholder="Search exercises..."
          disabled={false}
        />

        <p className="text-[11px] text-slate-500">
          Exercises are added with defaults. Tap any item below to customize sets, reps, rest, and cues.
        </p>
      </div>

      {/* Template items list */}
      {templateItems.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-wide text-slate-400">Template exercises</p>
            <span className="text-xs text-slate-400">{templateItems.length} exercises</span>
          </div>
          <div className={`${CARDS.nested} space-y-2`}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={templateItems.map((item, idx) => `${item.exerciseId}_${idx}`)}
                strategy={verticalListSortingStrategy}
              >
                {templateItems.map((item, idx) => {
                  const exerciseName = exerciseLookup.get(item.exerciseId)?.name || 'Exercise';
                  return (
                    <SortableItem
                      key={`${item.exerciseId}_${idx}`}
                      item={item}
                      idx={idx}
                      exerciseName={exerciseName}
                      onEdit={() => setDrawerItemIndex(idx)}
                      onRemove={() => onRemoveItem(idx)}
                    />
                  );
                })}
              </SortableContext>
            </DndContext>
          </div>
        </div>
      )}

      {/* Empty state */}
      {templateItems.length === 0 && (
        <div className={`text-center py-8 rounded-lg ${noExercises ? 'bg-red-900/10 border border-red-500/30' : 'text-slate-400'}`}>
          <p className={`text-sm ${noExercises ? 'text-red-400' : ''}`}>No exercises yet</p>
          <p className={`text-xs mt-1 ${noExercises ? 'text-red-400/80' : ''}`}>
            {noExercises ? 'Add at least one exercise to save' : 'Search above to add exercises to this template'}
          </p>
        </div>
      )}

      {/* Edit drawer */}
      <TemplateItemDrawer
        isOpen={drawerItemIndex !== null}
        onClose={handleDrawerClose}
        item={drawerItem}
        exercise={drawerExercise}
        onDelete={handleDrawerDelete}
      />
    </div>
  );
}
